<!--
  @component IO — Face 1 of the Global 4-face aggregator.
  tier:      face wrapper
  shape:     mirrors the lyn Global-face pattern (see Global.svelte)
  publishes: IO_KEY → IOContext → useIO()

  WHAT THIS IS
  ─────────────────────────────────────────────────────────────────

  The "external substrate" face — everything we installed via the
  published @liquidefy/* packages (nodes), curated into product-
  meaningful workflows (edges). Today: the deployment IO node + its
  4 edges. As more network packages land they aggregate here.

  Architecturally identical to lyn's IO.svelte: own KEY, own
  Context, own ready-gate, own EMPTY proxy sentinels, own onReady
  emit-once. Mounts the deployment node at root (so every portal
  and edge mounted below has its required ancestor). Captures the
  DeploymentIOContext and exposes it on `bundle.deployment`.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import type { DeploymentIOContext } from "./nodes/deployment/DeploymentIO.svelte";

  // Re-export the IO-tree contracts most consumers need. Explicit
  // named form (svelte's TS preprocess doesn't yet accept
  // `export type *`); additional types reachable from the source.
  export type {
    DeploymentIOContext,
    DeploymentNode,
    DeploymentCommandPort,
    DeploymentStreamEvent,
    DeploymentControlSurface,
  } from "./nodes/deployment/DeploymentIO.svelte";
  export type { LiveTimelineEdgeContext, LiveTimelineFilter }
    from "./edges/deployment/LiveTimelineEdge.svelte";
  export type { TopologyTruthEdgeContext }
    from "./edges/deployment/TopologyTruthEdge.svelte";
  export type { DeployRunEdgeContext, DeployRunStage }
    from "./edges/deployment/DeployRunEdge.svelte";
  export type { ReleaseLifecycleEdgeContext, ReleasePhase }
    from "./edges/deployment/ReleaseLifecycleEdge.svelte";

  /** The IO face bundle — what `useIO().bundle` exposes. Grows
   *  as more @liquidefy/* nodes land in network. */
  export interface IOBundle {
    /** The deployment node context (nodes + edges mounted as
     *  descendants reach their own contexts via their own keyed
     *  hooks; this is the root binding). */
    readonly deployment: DeploymentIOContext;
  }

  export interface IOContext {
    readonly ready: boolean;
    /** Live WS connectivity to the brain — true only while the command-port
     *  socket is OPEN. Distinct from `bundle.deployment.effecting`, which only
     *  means a port object is wired (always true in the browser). Screens gate
     *  "live" actions (e.g. Deploy now) on this so the control reflects reality. */
    readonly connected: boolean;
    readonly bundle: IOBundle;
  }

  export const IO_KEY: unique symbol = Symbol("global.io");

  export function useIO(): IOContext {
    const ctx = getContext<IOContext | undefined>(IO_KEY);
    if (!ctx) throw new Error("useIO() called outside of an IO component tree.");
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import DeploymentIO from "./nodes/deployment/DeploymentIO.svelte";
  import {
    createWebSocketCommandPort,
    type WebSocketCommandPort,
  } from "./hyperedges/network-deployment-transport.js";
  // Security node — mounted ambiently inside the deployment tree so the
  // cross-cutting security lenses (validate/guard/audit-trail) resolve via
  // their hooks from any screen. ValidationIO/GuardIO read the in-process
  // control plane; AuditTrailIO projects the server's security.* stream.
  import SecurityIO from "./nodes/security/SecurityIO.svelte";
  import ValidationIO from "./nodes/security/ValidationIO.svelte";
  import GuardIO from "./nodes/security/GuardIO.svelte";
  import AuditTrailIO from "./nodes/security/AuditTrailIO.svelte";
  import VaultStatusIO from "./nodes/security/VaultStatusIO.svelte";
  import AttestationIO from "./nodes/security/AttestationIO.svelte";
  import GateVerdictIO from "./nodes/security/GateVerdictIO.svelte";
  import ThreatIO from "./nodes/security/ThreatIO.svelte";
  import DebtIO from "./nodes/security/DebtIO.svelte";
  import IncidentIO from "./nodes/security/IncidentIO.svelte";
  import SecurityRoleSync from "./nodes/security/SecurityRoleSync.svelte";

  interface Props {
    /** Override the WS endpoint (defaults to `ws://localhost:3500/cmd`
     *  in dev, `wss://network.liquidefy.io/cmd` in prod). */
    endpoint?: string;
    /** Override the token source. Defaults to localStorage read of
     *  `network:consumer:auth` (the IdentityIO-managed JWT). */
    tokenSource?: () => Promise<string | null>;
    onReady?: (ctx: IOContext) => void;
    children?: Snippet;
  }

  function resolveDefaultEndpoint(): string {
    if (typeof window === "undefined") return "wss://network.liquidefy.io/cmd";
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "ws://localhost:3500/cmd";
    }
    return "wss://network.liquidefy.io/cmd";
  }
  async function defaultTokenSource(): Promise<string | null> {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem("network:consumer:auth");
  }

  const {
    endpoint = resolveDefaultEndpoint(),
    tokenSource = defaultTokenSource,
    onReady,
    children,
  }: Props = $props();

  let deploymentCtx = $state<DeploymentIOContext | null>(null);
  // Live WS connectivity, driven by the command port's connect callbacks.
  let connected = $state(false);

  // Construct the WebSocketCommandPort synchronously so DeploymentIO
  // (which captures `command` once on mount via untrack) sees a
  // stable port on first render. The WS itself opens async; methods
  // queue in the pre-open window. endpoint/tokenSource are
  // intentionally captured ONCE — re-creating the port on prop
  // change would tear down WS state mid-session.
  // svelte-ignore state_referenced_locally
  const port: WebSocketCommandPort | null =
    typeof window === "undefined"
      ? null
      : createWebSocketCommandPort({
          endpoint,
          tokenSource,
          onConnect: () => { connected = true; },
          onDisconnect: () => { connected = false; },
        });

  // Tear down on unmount.
  $effect(() => () => port?.close());

  const ready: boolean = $derived(deploymentCtx !== null);

  // EMPTY sentinel — pre-ready face access throws a self-describing
  // error pointing at the gate (the Global-tree convention).
  const EMPTY_HANDLER: ProxyHandler<object> = {
    get(_t, prop) {
      throw new Error(
        `IO.bundle.${String(prop)} accessed before ready. ` +
          `Gate on useIO().ready (or useGlobal().ready upstream).`,
      );
    },
  };
  const EMPTY_DEPLOYMENT = new Proxy({}, EMPTY_HANDLER) as unknown as DeploymentIOContext;

  const bundle: IOBundle = $derived({
    deployment: deploymentCtx ?? EMPTY_DEPLOYMENT,
  } satisfies IOBundle);

  const ctx: IOContext = {
    get ready() { return ready; },
    get connected() { return connected; },
    get bundle() { return bundle; },
  } satisfies IOContext;

  setContext(IO_KEY, ctx);

  let onReadyEmitted = false;
  $effect(() => {
    if (ready && !onReadyEmitted) {
      onReadyEmitted = true;
      onReady?.(ctx);
    }
  });
</script>

<DeploymentIO command={port ?? undefined} onReady={(c) => (deploymentCtx = c)}>
  <SecurityIO>
    <ValidationIO>
      <GuardIO>
        <AuditTrailIO>
          <VaultStatusIO>
            <AttestationIO>
              <GateVerdictIO>
                <ThreatIO>
                  <DebtIO>
                    <IncidentIO>
                      <SecurityRoleSync>
                        {@render children?.()}
                      </SecurityRoleSync>
                    </IncidentIO>
                  </DebtIO>
                </ThreatIO>
              </GateVerdictIO>
            </AttestationIO>
          </VaultStatusIO>
        </AuditTrailIO>
      </GuardIO>
    </ValidationIO>
  </SecurityIO>
</DeploymentIO>
